import { useEffect, useState, useCallback, useRef } from "react";
import { useQuery } from "react-query";
import { useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import useFetchData from "../../../../hooks/useFetchData";
import { groupByValue } from "../../../../utils";
import { titleize } from "inflected";
import { useAuth0 } from "@auth0/auth0-react";

/** ---------- small helpers ---------- */
const parseArrayParam = (searchParams, name) => {
  const v = searchParams.get(name);
  return v
    ? v
        .split(",")
        .map((s) => decodeURIComponent(s.trim()))
        .filter(Boolean)
    : [];
};

const layerIdsToColor = ["yampa-river-locations-circle"];

/** wait until all target layers exist (prevents map race) */
const waitForLayers = (map, ids, { retries = 20, interval = 200 } = {}) =>
  new Promise((resolve) => {
    if (!map) return resolve(false);
    let count = 0;
    const ok = () =>
      ids.every((id) => {
        try {
          return !!map.getLayer(id);
        } catch {
          return false;
        }
      });
    if (ok()) return resolve(true);
    const t = setInterval(() => {
      if (ok()) {
        clearInterval(t);
        resolve(true);
      } else if (++count >= retries) {
        clearInterval(t);
        resolve(false);
      }
    }, interval);
  });

/** ---------- hook ---------- */
const useGraphModeWithUrl = ({
  map,
  updateLayerFilters,
  layers,
  lastLocationIdClicked,
  setLastLocationIdClicked,
  setDataVizGraphModeVisible,
  graphModeVisible,
  setGraphModeVisible,
  setFilterValues,
  setDataVizVisible,
}) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const history = useHistory();

  const isInitializingFromUrl = useRef(false);
  const isUpdatingUrl = useRef(false);
  const mapReadyRef = useRef(false);

  const [legendVisible, setLegendVisible] = useState(true);
  const [lastLocationId, setLastLocationId] = useState(null);
  // Initialize inputValue from URL params if available
  const [inputValue, setInputValue] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get("mode");
    if (mode === "graph") {
      return parseInt(searchParams.get("recordCount") || "0", 10);
    }
    return 0;
  });

  /** read URL → initial filter state */
  const getInitialFilterValues = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get("mode");
    const defaults = {
      periodOfRecord: "full",
      analysis: "benchmark_scale_median",
      parameterGroups: [],
      threats: [],
      parameters: ["E. Coli"],
      recordCount: 0,
    };
    if (mode !== "graph") return defaults;

    return {
      periodOfRecord:
        searchParams.get("periodOfRecord") || defaults.periodOfRecord,
      analysis: searchParams.get("analysis") || defaults.analysis,
      parameterGroups: parseArrayParam(searchParams, "parameterGroups"),
      threats: parseArrayParam(searchParams, "threats"),
      parameters:
        parseArrayParam(searchParams, "parameters").length > 0
          ? parseArrayParam(searchParams, "parameters")
          : defaults.parameters,
      recordCount: parseInt(searchParams.get("recordCount") || "0", 10),
    };
  }, [location.search]);

  const [filterValuesGraphMode, setFilterValuesGraphMode] = useState(() =>
    getInitialFilterValues()
  );

  // Keep inputValue in sync with filterValuesGraphMode.recordCount
  useEffect(() => {
    if (inputValue !== filterValuesGraphMode.recordCount) {
      setInputValue(filterValuesGraphMode.recordCount);
    }
  }, [filterValuesGraphMode.recordCount]); // eslint-disable-line react-hooks/exhaustive-deps

  /** reflect filter state → URL (but not during URL bootstrap) */
  // reflect filter state → URL (avoid depending on location.search to prevent loops)
  useEffect(() => {
    if (isInitializingFromUrl.current || !graphModeVisible) return;

    isUpdatingUrl.current = true;

    // Start with existing search params to preserve other parameters like 'layers'
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("mode", "graph");
    searchParams.set("periodOfRecord", filterValuesGraphMode.periodOfRecord);
    searchParams.set("analysis", filterValuesGraphMode.analysis);
    searchParams.set("recordCount", String(filterValuesGraphMode.recordCount));

    const setArray = (key, arr) => {
      const clean = (arr || []).map((v) => encodeURIComponent(v)); // encode each part once
      if (clean.length) searchParams.set(key, clean.join(","));
    };

    setArray("parameterGroups", filterValuesGraphMode.parameterGroups);
    setArray("threats", filterValuesGraphMode.threats);
    setArray("parameters", filterValuesGraphMode.parameters);

    // only replace if something actually changed
    const newSearch = searchParams.toString();
    if (`?${newSearch}` !== location.search) {
      history.replace({
        pathname: location.pathname,
        search: `?${newSearch}`,
      });
    }

    // release the guard shortly after replace (prevents double-run)
    const tid = setTimeout(() => {
      isUpdatingUrl.current = false;
    }, 50);
    return () => clearTimeout(tid);
    // IMPORTANT: do NOT depend on location.search here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphModeVisible, filterValuesGraphMode, location.pathname, history]);

  /** if URL says mode=graph, show the toggle immediately */
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get("mode");
    if (mode === "graph" && !graphModeVisible) {
      isInitializingFromUrl.current = true;
      setGraphModeVisible(true);
      setDataVizVisible(false);
      setDataVizGraphModeVisible(true);
      // Also sync inputValue with recordCount from URL
      const recordCount = parseInt(searchParams.get("recordCount") || "0", 10);
      setInputValue(recordCount);
      const tid = setTimeout(() => {
        isInitializingFromUrl.current = false;
      }, 300);
      return () => clearTimeout(tid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only once on mount

  /** detect when the map is ready for paint/filter updates */
  useEffect(() => {
    let canceled = false;
    (async () => {
      if (!map) return;
      const ok = await waitForLayers(map, layerIdsToColor);
      if (!canceled) mapReadyRef.current = ok;
    })();
    return () => {
      canceled = true;
    };
  }, [map, layers]);

  /** static options */
  const periodOfRecords = [
    { value: "short", label: "Recent" },
    { value: "medium", label: "Last 10 Years" },
    { value: "full", label: "Full Period" },
  ];
  const analysisTypes = [
    { value: "benchmark_scale_median", label: "Median" },
    { value: "benchmark_scale_pctile85", label: "85th Percentile" },
  ];

  const [parameterGroups] = useFetchData(
    "list-parameter-groups-graph-mode",
    [],
    false
  );
  const [threats] = useFetchData("list-threats", [], false);

  const [hasFiltersLoaded, setHasFiltersLoaded] = useState(false);

  /** populate groups/threats either from URL or select-all */
  useEffect(() => {
    if (parameterGroups.length && threats.length) {
      const urlParams = getInitialFilterValues();
      if (
        urlParams.parameterGroups.length === 0 &&
        urlParams.threats.length === 0
      ) {
        onSelectAllParameterGroups();
        onSelectAllThreats();
      } else {
        const cleanedGroups = cleanParamGroups(urlParams.parameterGroups);
        const cleanedThreats = cleanThreats(urlParams.threats);
        setFilterValuesGraphMode((prev) => ({
          ...prev,
          parameterGroups: cleanedGroups,
          threats: cleanedThreats,
        }));
      }
      setHasFiltersLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterGroups, threats]);

  /** parameters list (depends on groups/threats & period) */
  const [hasParametersLoaded, setHasParametersLoaded] = useState(false);
  const { data: parameters, isFetching: isParametersFetching } = useQuery(
    [
      "list-parameters-graph-mode",
      filterValuesGraphMode.periodOfRecord,
      filterValuesGraphMode.parameterGroups,
      filterValuesGraphMode.threats,
      hasFiltersLoaded,
    ],
    async () => {
      if (!hasFiltersLoaded) return [];
      const payload = {
        periodOfRecord: filterValuesGraphMode.periodOfRecord,
        parameterGroups: filterValuesGraphMode.parameterGroups.map((g) =>
          getParameterGroupIndexByName(g)
        ),
        threats: filterValuesGraphMode.threats.map((t) =>
          getThreatIndexByName(t)
        ),
      };
      const { data } = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/api/list-parameters-graph-mode`,
        payload
      );
      return data;
    },
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  /** benchmark color list */
  const [benchmarkScaleColors] = useFetchData(
    "list-benchmark-scale-colors",
    [],
    false
  );

  const getHexColorForScore = useCallback(
    (score) =>
      benchmarkScaleColors.find((x) => x.benchmark_scale === score)
        ?.symbol_color || "black",
    [benchmarkScaleColors]
  );

  const [graphModeBenchmarkColors, setGraphModeBenchmarkColors] = useState([
    { name: `Above secondary benchmark`, color: `red` },
    { name: `Above benchmark`, color: `orange` },
    { name: `Approaching benchmark`, color: `yellow` },
    { name: `Below benchmark`, color: `MediumSeaGreen` },
    { name: `Below secondary benchmark`, color: `PaleTurquoise` },
    { name: `No benchmarks available`, color: `cornflowerblue` },
  ]);

  useEffect(() => {
    if (benchmarkScaleColors?.length) {
      setGraphModeBenchmarkColors([
        {
          name: `Above secondary benchmark`,
          color: benchmarkScaleColors[5]?.symbol_color ?? `red`,
        },
        {
          name: `Above benchmark`,
          color: benchmarkScaleColors[4]?.symbol_color ?? `orange`,
        },
        {
          name: `Approaching benchmark`,
          color: benchmarkScaleColors[3]?.symbol_color ?? `yellow`,
        },
        {
          name: `Below benchmark`,
          color: benchmarkScaleColors[2]?.symbol_color ?? `MediumSeaGreen`,
        },
        {
          name: `Below detection limits`,
          color: benchmarkScaleColors[1]?.symbol_color ?? `PaleTurquoise`,
        },
        {
          name: `No benchmarks available`,
          color: benchmarkScaleColors[0]?.symbol_color ?? `cornflowerblue`,
        },
      ]);
    }
  }, [benchmarkScaleColors]);

  const [graphModePopupVisible, setGraphModePopupVisible] = useState(true);
  const [graphModeLayersVisible, setGraphModeLayersVisible] = useState(true);

  const handleGraphModeLayersToggleClick = () => {
    if (!map) return;
    if (graphModeLayersVisible) {
      layers.forEach((layer) => {
        if (
          [
            "yampa-river-locations-circle",
            "yampa-river-locations-symbol",
          ].includes(layer.id)
        ) {
          map.setLayoutProperty(
            layer?.lreProperties?.name || layer.id,
            "visibility",
            "visible"
          );
        } else {
          map.setLayoutProperty(
            layer?.lreProperties?.name || layer.id,
            "visibility",
            "none"
          );
        }
      });
    } else {
      layers.forEach((layer) => {
        map.setLayoutProperty(
          layer?.lreProperties?.name || layer.id,
          "visibility",
          layer?.layout?.visibility === "visible" ? "visible" : "none"
        );
      });
    }
    setGraphModeLayersVisible(!graphModeLayersVisible);
  };

  const handleGraphModeClick = () => {
    if (!map) return;
    if (!graphModeVisible) {
      setDataVizVisible(false);
      setDataVizGraphModeVisible(true);
    } else {
      setFilterValues((prev) => {
        const newState = {
          ...prev,
          graphMode: { ...prev["graphModeReference"] },
        };
        updateLayerFilters(newState);
        return newState;
      });
      setDataVizGraphModeVisible(false);
      restoreOriginalLayerStyling();
      layers.forEach((layer) => {
        map.setLayoutProperty(
          layer?.lreProperties?.name || layer.id,
          "visibility",
          layer?.layout?.visibility === "visible" ? "visible" : "none"
        );
      });
    }

    // When entering graph mode, use the recordCount from filterValuesGraphMode
    // When exiting, reset to 0
    if (!graphModeVisible) {
      setInputValue(filterValuesGraphMode.recordCount);
    } else {
      setInputValue(0);
    }
    setGraphModePopupVisible(true);
    map.fire("closeAllPopups");
    setLastLocationIdClicked(null);
    setLastLocationId(null);
    setGraphModeVisible(!graphModeVisible);
    setGraphModeLayersVisible(true);
  };

  /** parameter auto-select vs URL-provided */
  const [hasGraphDataLoaded, setHasGraphDataLoaded] = useState(false);
  const [isAnalyticsTableDataLoading, setIsAnalyticsTableDataLoading] =
    useState(false);

  useEffect(() => {
    if (!isParametersFetching && parameters?.length && !hasParametersLoaded) {
      setHasParametersLoaded(true);
      setHasGraphDataLoaded(true);

      const urlParams = getInitialFilterValues();
      if (
        urlParams.parameters.length > 1 ||
        (urlParams.parameters.length === 1 &&
          urlParams.parameters[0] !== "E. Coli")
      ) {
        const cleaned = cleanParams(filterValuesGraphMode.parameters);
        setFilterValuesGraphMode((prev) => ({ ...prev, parameters: cleaned }));
        // force fetch once URL params have been applied (fix race)
        refetchGraphData();
      } else {
        setFilterValuesGraphMode((prev) => ({
          ...prev,
          parameters: parameters.map((x) => x.parameter_abbrev),
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParametersFetching, parameters]);

  /** keep params valid when the available list changes */
  useEffect(() => {
    if (hasParametersLoaded && parameters) {
      setFilterValuesGraphMode((prev) => ({
        ...prev,
        parameters: cleanParams(prev.parameters),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameters]);

  /** also re-clean when groups/threats change */
  useEffect(() => {
    if (hasParametersLoaded) {
      setFilterValuesGraphMode((prev) => {
        const cleanedParamGroups = cleanParamGroups(prev.parameterGroups);
        const cleanedThreats = cleanThreats(prev.threats);
        const cleanedParams = parameters
          ? cleanParams(prev.parameters)
          : prev.parameters;
        return {
          ...prev,
          parameterGroups: cleanedParamGroups,
          threats: cleanedThreats,
          parameters: cleanedParams,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterGroups, threats]);

  /** data for coloring the map */
  const {
    data,
    refetch: refetchGraphData,
    isFetching: isGraphDataFetching,
  } = useQuery(
    [
      "locations-map-graph-mode",
      filterValuesGraphMode.periodOfRecord,
      filterValuesGraphMode.recordCount,
      filterValuesGraphMode.parameters,
      hasParametersLoaded,
      isAuthenticated,
      graphModeVisible,
    ],
    async () => {
      if (
        !(
          hasParametersLoaded &&
          graphModeVisible &&
          filterValuesGraphMode.parameters.length > 0
        )
      )
        return [];
      setIsAnalyticsTableDataLoading(true);
      let headers = {};
      if (isAuthenticated) {
        const token = await getAccessTokenSilently();
        headers = { Authorization: `Bearer ${token}` };
      }
      const { data } = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/api/locations-map-graph-mode`,
        {
          periodOfRecord: filterValuesGraphMode.periodOfRecord,
          recordCount: filterValuesGraphMode.recordCount,
          parameters: filterValuesGraphMode.parameters.map((p) =>
            getParameterIndexByName(p)
          ),
        },
        { headers }
      );
      setHasGraphDataLoaded(true);
      return data;
    },
    { keepPreviousData: false, refetchOnWindowFocus: false }
  );

  /** clear all points / reset */
  const clearMapPoints = useCallback(() => {
    if (!map) return;
    setFilterValues((prev) => {
      const newState = {
        ...prev,
        graphMode: { ...prev["graphMode"], value: "" },
      };
      updateLayerFilters(newState);
      return newState;
    });

    layerIdsToColor.forEach((id) => {
      try {
        if (map.getLayer(id)) {
          map.setPaintProperty(id, "circle-stroke-width", 0);
          map.setPaintProperty(id, "circle-stroke-color", "transparent");
          map.setPaintProperty(id, "circle-opacity", 0.7);
          map.setPaintProperty(id, "circle-stroke-opacity", 0);
          map.setPaintProperty(id, "circle-color", "#1f77b4");
        }
      } catch {}
    });
  }, [map, setFilterValues, updateLayerFilters]);

  /** MAIN: apply colors/filters once everything is ready */
  useEffect(() => {
    const run = async () => {
      if (!graphModeVisible) return;
      if (isGraphDataFetching) return;
      if (!data || filterValuesGraphMode.parameters.length === 0) {
        clearMapPoints();
        setHasGraphDataLoaded(true);
        return;
      }
      // wait for layers to exist (prevents race on first load)
      if (!mapReadyRef.current) {
        const ok = await waitForLayers(map, layerIdsToColor);
        mapReadyRef.current = ok;
        if (!ok) return; // give up silently if layers never appear
      }
      setIsAnalyticsTableDataLoading(false);
      recolorPointsForLayers(data);
      fetchAnalyticsTableForLocation();
      fetchAnalyticsTimeSeriesForLocation();
    };
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    graphModeVisible,
    data,
    filterValuesGraphMode.parameters,
    isGraphDataFetching,
    map,
  ]);

  /** recolor again when analysis or recordCount changes (and map/data ready) */
  useEffect(() => {
    const run = async () => {
      if (
        graphModeVisible &&
        data &&
        filterValuesGraphMode.parameters.length > 0 &&
        !isGraphDataFetching
      ) {
        if (!mapReadyRef.current) {
          const ok = await waitForLayers(map, layerIdsToColor);
          mapReadyRef.current = ok;
          if (!ok) return;
        }
        recolorPointsForLayers(data);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterValuesGraphMode.analysis,
    filterValuesGraphMode.recordCount,
    isGraphDataFetching,
  ]);

  /** clear points when parameters become empty */
  useEffect(() => {
    if (graphModeVisible && filterValuesGraphMode.parameters.length === 0) {
      clearMapPoints();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValuesGraphMode.parameters]);

  /** recolor & filter */
  const recolorPointsForLayers = useCallback(
    (rows = null) => {
      if (!rows || !map) return;

      if (rows.length === 0) {
        clearMapPoints();
        return;
      }

      rows.sort((a, b) => (a.ndx > b.ndx ? 1 : b.ndx > a.ndx ? -1 : 0));

      const colorData = [];
      const locationValues = {};

      rows.forEach((row) => {
        if (
          filterValuesGraphMode.recordCount > 0 &&
          row.recordcount < filterValuesGraphMode.recordCount
        ) {
          return;
        }
        if (
          typeof locationValues[row.ndx] === "undefined" &&
          locationValues[row.ndx] !== null
        ) {
          locationValues[row.ndx] = -1;
        }
        if (
          filterValuesGraphMode.analysis === "benchmark_scale_pctile85" &&
          filterValuesGraphMode.analysis !== null
        ) {
          if (row.benchmark_scale_pctile85 > locationValues[row.ndx]) {
            locationValues[row.ndx] = row.benchmark_scale_pctile85;
          }
        } else {
          if (
            row.benchmark_scale_median > locationValues[row.ndx] &&
            filterValuesGraphMode.analysis !== null
          ) {
            locationValues[row.ndx] = row.benchmark_scale_median;
          }
        }
      });

      const locCount = Object.keys(locationValues).length;
      if (locCount === 0) {
        clearMapPoints();
        return;
      }

      for (const [loc_id, score] of Object.entries(locationValues)) {
        colorData.push(parseInt(loc_id, 10));
        colorData.push(getHexColorForScore(score));
      }

      setFilterValues((prev) => {
        const newState = {
          ...prev,
          graphMode: {
            ...prev["graphMode"],
            value: Object.keys(locationValues).map((x) => parseInt(x, 10)),
          },
        };
        updateLayerFilters(newState);
        return newState;
      });

      layerIdsToColor.forEach((id) => {
        if (colorData.length && map.getLayer(id)) {
          map.setPaintProperty(id, "circle-stroke-width", 2);
          map.setPaintProperty(id, "circle-stroke-color", "black");
          map.setPaintProperty(id, "circle-opacity", 1);
          map.setPaintProperty(id, "circle-stroke-opacity", 1);
          map.setPaintProperty(id, "circle-color", [
            "interpolate",
            ["linear"],
            ["get", "ndx"],
            ...colorData,
          ]);
        }
      });
    },
    [
      map,
      setFilterValues,
      clearMapPoints,
      filterValuesGraphMode.recordCount,
      filterValuesGraphMode.analysis,
      getHexColorForScore,
      updateLayerFilters,
    ]
  );

  /** restore defaults when leaving graph mode */
  const restoreOriginalLayerStyling = useCallback(() => {
    if (!map) return;
    layerIdsToColor.forEach((id) => {
      try {
        if (map.getLayer(id)) {
          map.setPaintProperty(id, "circle-radius", 8);
          map.setPaintProperty(id, "circle-stroke-width", 2);
          map.setPaintProperty(id, "circle-stroke-color", "black");
          map.setPaintProperty(id, "circle-opacity", 1);
          map.setPaintProperty(id, "circle-stroke-opacity", 1);
        }
      } catch {}
    });
  }, [map]);

  /** lookups / cleaners */
  const getParameterGroupIndexByName = (name) =>
    parameterGroups?.find((x) => x.parameter_group_name === name)
      ?.parameter_group_ndx;

  const getThreatIndexByName = (name) =>
    threats?.find((x) => x.threat_desc === name)?.threat_ndx;

  const getParameterIndexByName = (name) =>
    parameters?.find((x) => x.parameter_abbrev === name)?.parameter_ndx || null;

  const cleanParams = (params) => {
    if (!parameters) return params;
    const set = new Set(parameters.map((x) => x.parameter_abbrev));
    return params.filter((p) => set.has(p));
  };

  const cleanParamGroups = (params) => {
    if (!parameterGroups) return params;
    const valid = parameterGroups
      .filter((it) =>
        it.stats_period.includes(filterValuesGraphMode.periodOfRecord)
      )
      .map((x) => x.parameter_group_name);
    const set = new Set(valid);
    return params.filter((p) => set.has(p));
  };

  const cleanThreats = (params) => {
    if (!threats) return params;
    const set = new Set(threats.map((x) => x.threat_desc));
    return params.filter((p) => set.has(p));
  };

  /** filter handlers */
  const handleFilterValuesGraphMode = (name, value) => {
    if (
      ![
        "periodOfRecord",
        "analysis",
        "recordCount",
        "threats",
        "parameterGroups",
      ].includes(name)
    ) {
      setFilterValuesGraphMode((prevState) => {
        const existingVals = [...prevState[name]];
        const existingIndex = existingVals.indexOf(value);
        existingIndex > -1
          ? existingVals.splice(existingIndex, 1)
          : existingVals.push(value);

        return {
          ...prevState,
          [name]: existingVals,
        };
      });
    } else if (["threats", "parameterGroups"].includes(name)) {
      setFilterValuesGraphMode((prevState) => {
        let newValues = { ...prevState };
        newValues[name] = value.value;
        return newValues;
      });
    } else {
      // When recordCount changes via handler, also update inputValue to keep in sync
      if (name === "recordCount" && typeof value === "number") {
        setInputValue(value);
      }
      setFilterValuesGraphMode((prevState) => {
        return {
          ...prevState,
          [name]: value,
        };
      });
    }
  };

  const onSelectAllParameters = () =>
    setFilterValuesGraphMode((prev) => ({
      ...prev,
      parameters: parameters.map((x) => x.parameter_abbrev),
    }));

  const onSelectNoneParameters = () =>
    setFilterValuesGraphMode((prev) => ({ ...prev, parameters: [] }));

  const onSelectAllParameterGroups = () =>
    setFilterValuesGraphMode((prev) => ({
      ...prev,
      parameterGroups: parameterGroups.map((x) => x.parameter_group_name),
    }));

  const onSelectAllThreats = () =>
    setFilterValuesGraphMode((prev) => ({
      ...prev,
      threats: threats.map((x) => x.threat_desc),
    }));

  /** auto-select if URL didn’t provide */
  useEffect(() => {
    if (graphModeVisible !== null && !isInitializingFromUrl.current) {
      const urlParams = getInitialFilterValues();
      if (
        urlParams.parameters.length <= 1 &&
        urlParams.parameters[0] === "E. Coli"
      ) {
        onSelectAllParameters();
      }
      if (urlParams.parameterGroups.length === 0) onSelectAllParameterGroups();
      if (urlParams.threats.length === 0) onSelectAllThreats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphModeVisible]);

  /** analytics */
  const [analyticsResults, setAnalyticsResults] = useState(null);
  const [timeSeriesResults, setTimeSeriesResults] = useState(null);
  const [isTimeSeriesResultsLoading, setIsTimeSeriesResultsLoading] =
    useState(false);

  function fetchAnalyticsTableForLocation(location_index) {
    if (!graphModeVisible) return;
    if (!location_index) location_index = lastLocationId;
    if (!location_index) return;
    setAnalyticsResults(
      data?.filter(
        (item) =>
          item.stats_period === filterValuesGraphMode.periodOfRecord &&
          item.ndx === location_index
      )
    );
  }

  function fetchAnalyticsTimeSeriesForLocation(location_index) {
    setIsTimeSeriesResultsLoading(true);
    if (!graphModeVisible) return;
    if (!location_index) location_index = lastLocationId;
    if (!location_index) return;

    (async () => {
      try {
        const { data: line } = await axios.post(
          `${process.env.REACT_APP_ENDPOINT}/api/ts-daily-table-for-map-display/${location_index}`,
          {
            parameters: cleanParams(filterValuesGraphMode.parameters).map((x) =>
              getParameterIndexByName(x)
            ),
            periodOfRecord: filterValuesGraphMode.periodOfRecord,
          }
        );
        const groupedLineArray = groupByValue(line, "parameter");

        const { data: bar } = await axios.post(
          `${process.env.REACT_APP_ENDPOINT}/api/ts-annual-table-for-map-display/${location_index}`,
          {
            parameters: cleanParams(filterValuesGraphMode.parameters).map((x) =>
              getParameterIndexByName(x)
            ),
            periodOfRecord: filterValuesGraphMode.periodOfRecord,
          }
        );
        const groupedBarArray = groupByValue(bar, "parameter");
        setTimeSeriesResults({ line: groupedLineArray, bar: groupedBarArray });
      } catch (err) {
        if (axios.isCancel(err)) {
          // ignore
        } else {
          console.error(err);
        }
      } finally {
        setIsTimeSeriesResultsLoading(false);
      }
    })();
  }

  useEffect(() => {
    setLastLocationId(lastLocationIdClicked);
    fetchAnalyticsTableForLocation(lastLocationIdClicked);
    fetchAnalyticsTimeSeriesForLocation(lastLocationIdClicked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastLocationIdClicked]);

  /** export */
  const [isExportLoading, setIsExportLoading] = useState(false);
  const handleExportClick = (index) => {
    if (![2, 3].includes(index)) return;
    setIsExportLoading(true);

    (async () => {
      try {
        if (index === 2) {
          let { data: timeseriesData } = await axios.post(
            `${process.env.REACT_APP_ENDPOINT}/api/ts-daily-table-for-map-display`,
            {
              parameters: cleanParams(filterValuesGraphMode.parameters).map(
                (x) => getParameterIndexByName(x)
              ),
              periodOfRecord: filterValuesGraphMode.periodOfRecord,
              indexes: [...new Set(data.map((item) => item.ndx))],
            }
          );

          const csv = [
            [
              `"Results for parameters: ${filterValuesGraphMode.parameters.join(
                ", "
              )}"`,
            ],
            [
              `"Time Series Data for the ${titleize(
                filterValuesGraphMode.periodOfRecord
              )} Period"`,
            ],
            [
              "Location ID",
              "Location Name",
              "Parameter",
              "Activity Date",
              "Data Value",
              "Units",
              "Organization",
            ],
            ...timeseriesData.map((item) => [
              item.location_id,
              `"${item.location_name}"`,
              `"${item.param_abbrev}"`,
              item.collect_date,
              item.result,
              item.units,
              `"${item.organization}"`,
            ]),
          ]
            .map((e) => e.join(","))
            .join("\n");

          const a = document.createElement("a");
          a.href = "data:attachment/csv," + encodeURIComponent(csv);
          a.target = "_blank";
          a.download = `Time Series Data for the ${titleize(
            filterValuesGraphMode.periodOfRecord
          )} Period.csv`;
          document.body.appendChild(a);
          a.click();
        }

        if (index === 3) {
          let tableData = [...data];
          const csv = [
            [
              `"Results for parameters: ${filterValuesGraphMode.parameters.join(
                ", "
              )}"`,
            ],
            [
              `"Stats & Benchmarks Data for the ${titleize(
                filterValuesGraphMode.periodOfRecord
              )} Period"`,
            ],
            [
              "Location ID",
              "Location Name",
              "Parameter",
              "Units",
              "Count of Results (Statistics)",
              "85th or 15th percentile",
              "Benchmark Classification: 85th/15th",
              "Median",
              "Benchmark Classification: Median",
              "Analysis Period (Statistics)",
              "From",
              "To",
              "Benchmark 0",
              "Benchmark 1",
              "Benchmark 2",
              "Benchmark 3",
              "Benchmark 4",
              "Trend (all data)",
              "Organizations",
            ],
            ...tableData.map((item) => [
              item.location_id,
              `"${item.location_name}"`,
              `"${item.parameter}"`,
              item.units,
              item.recordcount,
              item.pctile85,
              item.benchmark_scale_pctile85,
              item.median,
              item.benchmark_scale_median,
              item.stats_period,
              item.por_start,
              item.por_end,
              item.bmk_line0,
              item.bmk_line1,
              item.bmk_line2,
              item.bmk_line3,
              item.bmk_line4,
              item.trend,
              `"${item.organizations}"`,
            ]),
          ]
            .map((e) => e.join(","))
            .join("\n");

          const a = document.createElement("a");
          a.href = "data:attachment/csv," + encodeURIComponent(csv);
          a.target = "_blank";
          a.download = `Stats & Benchmarks Data for the ${titleize(
            filterValuesGraphMode.periodOfRecord
          )} Period.csv`;
          document.body.appendChild(a);
          a.click();
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsExportLoading(false);
      }
    })();
  };

  return {
    filterValuesGraphMode,
    periodOfRecords,
    analysisTypes,
    parameterGroups,
    threats,
    parameters,
    handleFilterValuesGraphMode,
    onSelectAllParameters,
    onSelectNoneParameters,
    handleGraphModeClick,
    hasGraphDataLoaded,
    analyticsResults,
    timeSeriesResults,
    isTimeSeriesResultsLoading,
    getHexColorForScore,
    isAnalyticsTableDataLoading,
    legendVisible,
    setLegendVisible,
    graphModeBenchmarkColors,
    handleGraphModeLayersToggleClick,
    graphModeLayersVisible,
    graphModePopupVisible,
    setGraphModePopupVisible,
    inputValue,
    setInputValue,
    handleExportClick,
    isExportLoading,
  };
};

export default useGraphModeWithUrl;
